@if (isLoading) {
<div class="loader-overlay" role="status" aria-live="polite">
  <div class="heart-wrapper" aria-hidden="true">
    <svg class="heart-svg" viewBox="0 0 24 24">
      <path class="heart-shape" d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5
               2 6 4 4 6.5 4 8.24 4 9.91 5 10.54 6.36
               11.17 5 12.76 4 14.5 4 17 4 19 6 19 8.5
               c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
      <polyline class="ekg" points="3 12 7 12 9 8 11 15 13 6 15 12 21 12" fill="none" />
    </svg>
  </div>
  <p class="loader-text mt-3 fw-semibold text-dark">Loading manager dashboard...</p>
</div>
}

@if (!isLoading) {
<div class="manager-dashboard-container">
  <app-portal-navigation portalName="Manager Portal" portalIcon="bi-clipboard2-pulse-fill" portalClass="text-primary"
    collapseId="managerMenu" [firstName]="userProfile?.firstName ?? ''" [lastName]="userProfile?.lastName ?? ''"
    [additionalItems]="profileNavigationItems" [clinicMemberships]="clinicMemberships"
    (switchClinic)="onSwitchClinic($event)">
  </app-portal-navigation>

  <div class="container-fluid p-3 p-md-4">
    <!-- Hero Banner -->
    <div class="booking-hero card border-0 mb-4">
      <div class="booking-hero__content">
        <div>
          <div class="booking-hero__eyebrow">Practice Administration</div>
          <h2 class="booking-hero__title mb-2">
            Manager Overview &bull; {{ userProfile?.firstName }} {{ userProfile?.lastName }}
          </h2>
          <div class="booking-hero__meta">
            <span><i class="bi bi-shield-lock me-1 text-primary"></i>Administrative Control</span>
            <span><i class="bi bi-envelope me-1"></i>{{ userProfile?.email }}</span>
            <span><i class="bi bi-telephone me-1"></i>{{ userProfile?.phoneNumber }}</span>
          </div>
        </div>

        <div class="booking-hero__status">
          <span class="status-pill" [ngClass]="clinicStatusDisplay.pillClass">
            <i class="bi me-1" [ngClass]="clinicStatusDisplay.icon"></i>{{ clinicStatusDisplay.label }}
          </span>
          <div class="booking-hero__status-note">
            Total Practice Bookings: {{ reportSummary?.total ?? '0' }}
          </div>
        </div>
      </div>

      <!-- Pending Subscription Invoice Banner -->
      @if (subscriptionPaymentStatus?.paymentRequired) {
      <div
        class="hero-notice-banner p-3 mt-3 rounded-4 d-flex flex-wrap align-items-center justify-content-between gap-3"
        style="
              background: linear-gradient(135deg, #fff9e6 0%, #ffefc2 100%);
              border-left: 5px solid #ffc107;
              box-shadow: 0 4px 12px rgba(217, 119, 6, 0.12);
            ">
        <div class="d-flex align-items-center gap-3">
          <div
            class="bg-warning text-dark rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
            style="width: 44px; height: 44px; box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);">
            <i class="bi bi-exclamation-triangle-fill fs-5"></i>
          </div>
          <div>
            <div class="fw-bold text-dark fs-6">
              Subscription Invoice Pending: R{{ subscriptionPaymentStatus?.amount | number: '1.2-2' }}
            </div>
            <div class="text-secondary small">
              @if (subscriptionPaymentStatus?.payByDate) {
              Invoice payment is due on {{ subscriptionPaymentStatus?.payByDate | date: 'dd MMM yyyy' }}.
              } @else {
              Please complete payment to maintain active practice subscription.
              }
            </div>
          </div>
        </div>
        <a [routerLink]="['/dashboard/settings']" [queryParams]="{ tab: 'subscription' }"
          class="btn btn-warning btn-sm px-3 py-2 fw-bold text-dark rounded-3 shadow-sm d-inline-flex align-items-center gap-2">
          <i class="bi bi-credit-card-fill"></i> Pay Now
        </a>
      </div>
      }

      <!-- Failed Subscription Invoice Banner -->
      @if (hasLatestInvoiceFailed) {
      <div
        class="hero-notice-banner p-3 mt-3 rounded-4 d-flex flex-wrap align-items-center justify-content-between gap-3"
        style="
              background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
              border-left: 5px solid #ef4444;
              box-shadow: 0 4px 12px rgba(239, 68, 68, 0.12);
            ">
        <div class="d-flex align-items-center gap-3">
          <div
            class="bg-danger text-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
            style="width: 44px; height: 44px; box-shadow: 0 2px 6px rgba(220, 38, 38, 0.2);">
            <i class="bi bi-x-octagon-fill fs-5"></i>
          </div>
          <div>
            <div class="fw-bold text-dark fs-6">Subscription Invoice Payment Failed</div>
            <div class="text-secondary small">
              Your latest subscription payment failed. Please retry payment or contact support to avoid service
              disruption.
            </div>
          </div>
        </div>
        <a [routerLink]="['/dashboard/settings']" [queryParams]="{ tab: 'subscription' }"
          class="btn btn-danger btn-sm px-3 py-2 fw-bold text-white rounded-3 shadow-sm d-inline-flex align-items-center gap-2">
          <i class="bi bi-arrow-repeat"></i> Resolve Payment
        </a>
      </div>
      }

      <!-- Trial Active Banner -->
      @if (isTrialActive) {
      <div
        class="hero-notice-banner p-3 mt-3 rounded-4 d-flex flex-wrap align-items-center justify-content-between gap-3"
        style="
              background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
              border-left: 5px solid #3b82f6;
              box-shadow: 0 4px 12px rgba(59, 130, 246, 0.12);
            ">
        <div class="d-flex align-items-center gap-3">
          <div
            class="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
            style="width: 44px; height: 44px; box-shadow: 0 2px 6px rgba(13, 110, 253, 0.2);">
            <i class="bi bi-info-circle-fill fs-5"></i>
          </div>
          <div>
            <div class="fw-bold text-dark fs-6">Trial Package Active</div>
            <div class="text-secondary small">
              Your practice can add one staff member during the trial. Doctor invitations are unavailable until the
              trial ends.
            </div>
          </div>
        </div>
      </div>
      }

      <!-- Inactive / Suspended Practice Banner -->
      @if (!isClinicActive) {
      <div
        class="hero-notice-banner p-3 mt-3 rounded-4 d-flex flex-wrap align-items-center justify-content-between gap-3"
        style="
              background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
              border-left: 5px solid #3b82f6;
              box-shadow: 0 4px 12px rgba(59, 130, 246, 0.12);
            ">
        <div class="d-flex align-items-center gap-3">
          <div
            class="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
            style="width: 44px; height: 44px; box-shadow: 0 2px 6px rgba(13, 110, 253, 0.2);">
            <i class="bi bi-info-circle-fill fs-5"></i>
          </div>
          <div>
            <div class="fw-bold text-dark fs-6">Practice Status: {{ clinicStatusDisplay.label }}</div>
            <div class="text-secondary small">
              Menu options are unavailable and cannot be selected until the practice status is active.
              @if (clinicSuspensionComments) {
              <span class="d-block mt-1 text-danger fw-semibold">{{ clinicSuspensionComments }}</span>
              }
            </div>
          </div>
        </div>
      </div>
      }
    </div>

    <!-- Alert: Receptionists Not Linked to Any Doctor -->
    @if (unlinkedReceptionists.length > 0) {
    <div class="alert alert-warning d-flex align-items-start gap-3 mb-4" role="alert">
      <i class="bi bi-exclamation-triangle-fill fs-5 flex-shrink-0 mt-1"></i>
      <div>
        <strong>Receptionists Not Linked to Any Doctor</strong>
        <p class="mb-2">
          {{ unlinkedReceptionists.length }}
          {{ unlinkedReceptionists.length === 1 ? 'receptionist is' : 'receptionists are' }}
          not assigned to work with any doctor. They need to be linked to proceed:
        </p>
        <ul class="mb-0 ps-3">
          @for (receptionist of unlinkedReceptionists; track receptionist.id) {
          <li>
            <strong>{{ receptionist.firstName }} {{ receptionist.lastName }}</strong> ({{
            receptionist.email
            }}) — <span class="text-muted small">No doctor assignment</span>
          </li>
          }
        </ul>
      </div>
    </div>
    }

    <!-- Password Expiration Reminder Alert -->
    @if (usersWithExpiringPasswords.length > 0) {
    <div class="alert alert-info d-flex align-items-start gap-3 mb-4" role="alert">
      <i class="bi bi-exclamation-triangle-fill fs-5 flex-shrink-0 mt-1"></i>
      <div>
        <strong>Password Expiration Reminders</strong>
        <p class="mb-2">
          {{ usersWithExpiringPasswords.length }}
          {{
          usersWithExpiringPasswords.length === 1 ? 'staff member has' : 'staff members have'
          }}
          passwords expiring within the next 14 days:
        </p>
        <ul class="mb-0 ps-3">
          @for (user of usersWithExpiringPasswords; track user.id) {
          <li>
            <strong>{{ user.firstName }} {{ user.lastName }}</strong> ({{ user.email }}) —
            expires in {{ getDaysUntilExpiry(user.passwordExpiresAt) }}
            {{ getDaysUntilExpiry(user.passwordExpiresAt) === 1 ? 'day' : 'days' }}
          </li>
          }
        </ul>
      </div>
    </div>
    }

    <!-- Quick Stats Metric Matrix -->
    <div class="row g-2 mb-3">
      <div class="col-6 col-sm-4 col-lg-2">
        <div class="card metric-card h-100">
          <div class="text-muted small fw-bold text-uppercase mb-1">Completed</div>
          <div class="fs-5 fw-bold text-success">{{ reportSummary?.completed ?? '—' }}</div>
        </div>
      </div>

      <div class="col-6 col-sm-4 col-lg-2">
        <div class="card metric-card h-100">
          <div class="text-muted small fw-bold text-uppercase mb-1">Cancelled</div>
          <div class="fs-5 fw-bold text-danger">{{ reportSummary?.cancelled ?? '—' }}</div>
        </div>
      </div>

      <div class="col-6 col-sm-4 col-lg-2">
        <div class="card metric-card h-100">
          <div class="text-muted small fw-bold text-uppercase mb-1">Pending</div>
          <div class="fs-5 fw-bold text-warning">{{ reportSummary?.pending ?? '—' }}</div>
        </div>
      </div>

      <div class="col-6 col-sm-4 col-lg-2">
        <div class="card metric-card h-100">
          <div class="text-muted small fw-bold text-uppercase mb-1">Total</div>
          <div class="fs-5 fw-bold text-primary">{{ reportSummary?.total ?? '—' }}</div>
        </div>
      </div>

      <div class="col-6 col-sm-4 col-lg-2">
        <div class="card metric-card h-100">
          <div class="text-muted small fw-bold text-uppercase mb-1">No Shows</div>
          <div class="fs-5 fw-bold text-warning">{{ reportSummary?.noShow ?? '—' }}</div>
        </div>
      </div>
    </div>

    <div class="row g-4">
      <!-- LEFT: Profile & Quick Summary -->
      <div class="col-12 col-xl-4 d-flex flex-column gap-3">
        <div class="card booking-card">
          <div class="card-header booking-card__header">
            <div>
              <div class="booking-card__eyebrow">User Profile</div>
              <h4 class="sub-title mb-0">Manager Identity</h4>
            </div>
          </div>

          <div class="card-body p-4 d-flex align-items-center gap-3">
            <div class="manager-avatar-circle flex-shrink-0">
              {{ userProfile?.firstName?.charAt(0) }}{{ userProfile?.lastName?.charAt(0) }}
            </div>

            <div class="min-w-0">
              <h5 class="fw-bold text-dark mb-1 text-truncate">
                {{ userProfile?.firstName }} {{ userProfile?.lastName }}
              </h5>
              <div class="text-muted small text-truncate mb-1">
                <i class="bi bi-envelope me-1"></i>{{ userProfile?.email }}
              </div>
              <div class="text-muted small">
                <i class="bi bi-phone me-1"></i>{{ userProfile?.phoneNumber }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- RIGHT: User Management -->
      <div class="col-12 col-xl-8 d-flex flex-column gap-3">
        <div class="card booking-card flex-grow-1">
          <div class="card-header booking-card__header">
            <div>
              <div class="booking-card__eyebrow">User Management</div>
              <h4 class="sub-title mb-0">
                <i class="bi bi-people me-2 text-primary"></i>Practice Users
              </h4>
            </div>

            <button class="btn btn-sm btn-outline-primary" [disabled]="!isClinicActive"
              [class.opacity-50]="!isClinicActive" [attr.title]="
                  !isClinicActive ? 'Not available until practice is active' : 'Manage Users'
                " (click)="isClinicActive && navigateToManageUsers()">
              <i class="bi bi-gear me-1"></i>Manage Users
            </button>
          </div>

          <div class="card-body p-0">
            @if (usersLoading) {
            <div class="p-5 text-center text-muted">
              <span class="spinner-border spinner-border-sm me-2 text-primary"></span>
              Loading practice users...
            </div>

            } @else if (users.length === 0) {
            <div class="empty-state">
              <i class="bi bi-inbox fs-2 d-block mb-1 text-primary"></i>
              <h6>No Practice Users</h6>
              <p class="text-muted small mb-0">
                Invite or add staff members to build your practice team.
              </p>
            </div>

            } @else {
            <div class="list-group list-group-flush">
              @for (user of users; track user.id) {
              <div class="list-group-item p-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div class="d-flex align-items-center gap-3">
                  <div class="activity-icon-badge">
                    <i class="bi bi-person-fill text-primary"></i>
                  </div>
                  <div>
                    <strong class="text-dark">{{ user.firstName }} {{ user.lastName }}</strong>
                    <div class="text-muted small">
                      {{ user.email }} &bull; {{ user.role }}
                    </div>
                    @if (user.role === 'RECEPTION' && !user.clinicMembershipId) {
                    <div class="mt-1">
                      <span class="badge bg-danger-subtle text-danger border border-danger-subtle">
                        <i class="bi bi-exclamation-circle me-1"></i>Not linked to any
                        doctor
                      </span>
                    </div>
                    }
                  </div>
                </div>

                <div class="d-flex align-items-center gap-2">
                  <span class="status-pill" [class.status-completed]="user.active"
                    [class.status-cancelled]="!user.active">
                    {{ user.active ? 'Active' : 'Inactive' }}
                  </span>
                  <div class="btn-group">
                    @if (!isDoctor(user)) {
                    <button class="btn btn-sm btn-outline-primary" (click)="navigateToManageUsers()"
                      [disabled]="!isClinicActive" [title]="
                                !isClinicActive
                                  ? 'Not available until practice is active'
                                  : 'Edit user'
                              ">
                      <i class="bi bi-pencil"></i>
                    </button>
                    <button class="btn btn-sm btn-outline-warning" (click)="resetPassword(user)"
                      [disabled]="!isClinicActive || isResettingPasswordId === user.id" [title]="
                                !isClinicActive
                                  ? 'Not available until practice is active'
                                  : 'Reset password'
                              ">
                      @if (isResettingPasswordId === user.id) {
                      <span class="spinner-border spinner-border-sm" role="status"></span>
                      } @else {
                      <i class="bi bi-key"></i>
                      }
                    </button>
                    }
                    @if (canToggleUserStatus(user)) {
                    <button class="btn btn-sm btn-outline-danger" (click)="toggleUserStatus(user)"
                      [disabled]="!isClinicActive || isTogglingStatusId === user.id" [title]="
                                !isClinicActive
                                  ? 'Not available until clinic is active'
                                  : isDoctor(user)
                                    ? 'Toggle clinic membership status'
                                    : 'Toggle active status'
                              ">
                      @if (isTogglingStatusId === user.id) {
                      <span class="spinner-border spinner-border-sm" role="status"></span>
                      } @else {
                      <i class="bi bi-power"></i>
                      }
                    </button>
                    }
                  </div>
                </div>
              </div>
              }
            </div>
            }
          </div>
        </div>
      </div>
    </div>
    <!-- Footer Link -->
    <div class="text-center mt-5 mb-2 d-flex justify-content-center gap-4">
      <a href="javascript:void(0)" class="text-muted small text-decoration-underline" data-bs-toggle="modal"
        data-bs-target="#privacyPolicyModal">
        <i class="bi bi-file-text me-1"></i>Terms &amp; Conditions
      </a>
      <a href="javascript:void(0)" class="text-muted small text-decoration-underline" data-bs-toggle="modal"
        data-bs-target="#contactUsModal">
        <i class="bi bi-envelope me-1"></i>Contact Us
      </a>
    </div>
  </div>
</div>

<!-- Privacy Policy / Terms & Conditions Modal -->
<app-terms-and-conditions-modal></app-terms-and-conditions-modal>

<!-- Contact Us Modal -->
<app-contact-us-modal></app-contact-us-modal>
}